import React from 'react';
interface BaseProps {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  hint?: string;
}
interface TextFieldProps extends BaseProps {
  value: string;
  onChange?: (v: string) => void;
  type?: string;
  mono?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
}
function Label({
  id,
  label,
  required
}: {
  id: string;
  label: string;
  required?: boolean;
}) {
  return <label htmlFor={id} className="mb-1 block text-[13px] font-medium text-slate-700" data-id="element-1273" data-mp="htf1anax13gk">
      {label} {required && <span className="text-red-600" data-id="element-1274" data-mp="qsa4dqinxxfn">*</span>}
    </label>;
}
function Help({
  error,
  hint
}: {
  error?: string;
  hint?: string;
}) {
  if (error) return <p className="mt-1 text-[11px] font-medium text-red-600" data-id="element-1275" data-mp="luh1b6cnpjjl">{error}</p>;
  if (hint) return <p className="mt-1 text-[11px] text-slate-400" data-id="element-1276" data-mp="2skojyixqipsw">{hint}</p>;
  return null;
}
const inputCls = (error?: string, readOnly?: boolean) => `h-11 w-full rounded-lg border px-3 text-[15px] text-navy-900 outline-none transition focus:border-navy-600 focus:ring-2 focus:ring-navy-100 ${error ? 'border-red-400 bg-red-50/40' : 'border-slate-300'} ${readOnly ? 'bg-slate-100 text-slate-600' : 'bg-white'}`;
export function TextField({
  id,
  label,
  required,
  error,
  hint,
  value,
  onChange,
  type = 'text',
  mono,
  readOnly,
  placeholder,
  inputMode
}: TextFieldProps) {
  return <div data-id="element-1277" data-mp="2p1z77vh9tdsp">
      <Label id={id} label={label} required={required} data-id="element-1278" data-mp="2p28yrnhyrt0i" />
      <input id={id} type={type} value={value} readOnly={readOnly} placeholder={placeholder} inputMode={inputMode} onChange={e => onChange?.(e.target.value)} aria-invalid={Boolean(error)} className={`${inputCls(error, readOnly)} ${mono ? 'font-mono text-[14px]' : ''}`} data-id="element-1279" data-mp="2p3tsxc0d99xu" />
      <Help error={error} hint={hint} data-id="element-1280" data-mp="14mnrf6pcshag" />
    </div>;
}
interface SelectFieldProps extends BaseProps {
  value: string;
  options: string[];
  onChange?: (v: string) => void;
}
export function SelectField({
  id,
  label,
  required,
  error,
  hint,
  value,
  options,
  onChange
}: SelectFieldProps) {
  return <div data-id="element-1281" data-mp="wlznv2t619dt">
      <Label id={id} label={label} required={required} data-id="element-1282" data-mp="wm7yeo1tg2lu" />
      <select id={id} value={value} onChange={e => onChange?.(e.target.value)} className={inputCls(error)} data-id="element-1283" data-mp="1bhxiggnfy5nx">
        {options.map(o => <option key={o} data-id="element-1284" data-mp="16hj64met49k3">{o}</option>)}
      </select>
      <Help error={error} hint={hint} data-id="element-1285" data-mp="1kidfb79oiejt" />
    </div>;
}
interface TextAreaProps extends BaseProps {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
}
export function TextAreaField({
  id,
  label,
  required,
  error,
  hint,
  value,
  onChange,
  rows = 3,
  placeholder
}: TextAreaProps) {
  return <div data-id="element-1286" data-mp="1pawta5ca3bp5">
      <Label id={id} label={label} required={required} data-id="element-1287" data-mp="1pa3srm3wxifg" />
      <textarea id={id} rows={rows} value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} className={`w-full rounded-lg border p-3 text-[15px] text-navy-900 outline-none focus:border-navy-600 focus:ring-2 focus:ring-navy-100 ${error ? 'border-red-400' : 'border-slate-300'}`} data-id="element-1288" data-mp="238v7naqln84n" />
      <Help error={error} hint={hint} data-id="element-1289" data-mp="2mt2cyu3knket" />
    </div>;
}
export function CheckRow({
  checked,
  onChange,
  children
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  children: React.ReactNode;
}) {
  return <label className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 text-[13px] leading-relaxed ${checked ? 'border-bdgreen-200 bg-bdgreen-50/60' : 'border-slate-200 bg-white'}`} data-id="element-1290" data-mp="2dwb8btayex8m">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-bdgreen-600" data-id="element-1291" data-mp="23y5cntfqqnc7" />
      <span className="text-navy-900" data-id="element-1292" data-mp="1u7ize5vhy8wo">{children}</span>
    </label>;
}